import React, { useState } from 'react';
import superagent from "superagent";
import PropTypes from 'prop-types'
import moment from 'moment'
import R from 'ramda'
import { Menu, Grid, Button, Accordion, Segment, Label } from "semantic-ui-react";
import { SESSION_LENGTH_VALUES, PAGES, AUTH_API_URL } from '_constants';
import { licenseIsUpgradable, getShortSublicensesLabelWithExpiry } from '_utils/licenses';
import { ProductActivatedInfoText, ProductDeactivatedByText } from "./util";

import {
  STATUS,
  MECHANISMS,
  MAX_VERSIONS_DISPLAYNAME,
  CURRENT_MAX_VERSION,
} from '../../../server/constants'

import {
  toTitle,
  notNilOrEmpty,
  productNameToUpper,
  getFsUrl
} from '_utils'

import LineItem from '_views/lineItem'
import Settings from './settings'
import Sublicenses from './sublicenses';
import Fastspring from './fastspring'
import Machines from '_views/machines'
import ActivationCode from './activationCode'
import Serial from './serial'
import Transfer from './transfer'
import Upgrade from './upgrade'
import LicenseResellerInfo from './reseller'
import LicenseInstitutionInfo from './institution'
import LicenseUpgradeInfo from './upgrades'
import useCanAdminLicenses from '../../hooks/useCanAdminLicenses';

const getProductType = R.propOr('Unknown', 'type');
const getProductName = R.ifElse(
  R.propEq('type', 'subscription'),
  R.always('Max'),
  R.compose(
    toTitle,
    R.defaultTo('unknown'),
    R.prop('name')
  )
)

const getProductExpires = R.prop('expires_on')
const getProductExpiresLabel = R.compose(
  R.ifElse(
    x => moment(x).toDate() > new Date(),
    R.always('Expires'),
    R.always(<u>Expired</u>)
  ),
  R.propOr('', 'expires_on')
)

const getCreatedOn = R.prop('activated_on')

const getMachines = R.propOr([], 'machines')
const getMachinesLength = R.pathOr(0, ['machines', 'length'])
const getSessionLengthToText = R.compose(
  R.prop('text'),
  R.call(R.flip(R.find)(SESSION_LENGTH_VALUES)),
  R.propEq('value'),
  R.propOr(60 * 24 * 180, 'session_length_min')
)

const getIlokToText = R.compose(
  R.ifElse(
    R.both(R.is(Array), notNilOrEmpty),
    x => ', ' + x.length + ' ilok(s)',
    R.always('')
  ),
  R.prop('ilok_accounts')
)

const getMachineCount = R.propOr(4, 'machine_count')

const getSettingsPanel = (license, onModifyLicense) => ({
  title: `Settings : ${getSessionLengthToText(license)}, ${getMachineCount(license)} machines${getIlokToText(license)}`,
  content: {
    content: (<Settings license={license} onModifyLicense={onModifyLicense} />),
    key: 'lic_' + license._id + '_settings'
  }
})

const getMachinesPanel = R.ifElse(
  R.pathSatisfies(R.gt(R.__, 0), ['license', 'machines', 'length']),
  ({license, onRemoveLicenseMachine}) => ({
    title: `Machines : ${getMachinesLength(license)} used`,
    content: {
      content: (<Machines machines={getMachines(license)} objectId={license._id} onRemoveMachine={onRemoveLicenseMachine} fieldName='licenseId' />),
      key: 'lic_' + license._id + '_machines'
    }
  }),
  () => null
)

const getFastspringProductName = lic => {
  const n = R.pathOr('unknown', ['activated_info', 'fastspring', 'name'], lic)
  return lic.name === 'max8' && lic.type === 'full' && /7/.test(n)
    ? productNameToUpper(n.replace('7', '8')) + ' (free upgrade from max 7)'
    : lic.type === 'subscription'
      ? productNameToUpper(n.replace(/\d\s/, ''))
      : productNameToUpper(n)
}

const getFastspringPanel = R.ifElse(
  R.pathEq(['license', 'activated_info', 'mechanism'], 'fastspring'),
  ({license, setNavigation}) => ({
    title: 'Fastspring : ' + getFastspringProductName(license),
    content: {
      content: (<Fastspring license={license} setNavigation={setNavigation} />),
      key: 'lic_' + license._id + '_fs'
    }
  }),
  () => null
)

const mechIsAc = R.compose(
  R.contains(R.__, [MECHANISMS.INSTITUTION_AC, MECHANISMS.RESELLER_AC]),
  R.path(['activated_info', 'mechanism'])
)

const getActivationCodePanel = R.ifElse(
  mechIsAc,
  (license, setNavigation) => ({
    title: 'Activation Code',
    content: {
      content: <ActivationCode license={license} setNavigation={setNavigation} />,
      key: 'lic_' + license._id + '_ac'
    }
  }),
  () => null
)

const getSerialPanel = R.ifElse(
  R.pathEq(['activated_info', 'mechanism'], 'serial_upgrade'),
  license => ({
    title: 'Free Upgrade From Max 6',
    content: {
      content: <Serial license={license} />,
      key: 'lic_' + license._id + '_serial'
    }
  }),
  () => null
)

const getSubLicensesPanel = ({ license, onAddSublicense, onRemoveSublicense }) => ({
  title: `Add Ons${getShortSublicensesLabelWithExpiry(license.sublicenses)}`,
  content: {
    content: <Sublicenses
      licenseId={license._id}
      licenseName={ license.name }
      sublicenses={license.sublicenses}
      onAddSublicense={onAddSublicense}
      onRemoveSublicense={onRemoveSublicense}
    />,
    key: `lic_${license.id}_sublicenses`
  }
});

const getResellerPanel = ({ license, setNavigation }) => license.reseller ? ({
  title: `Reseller: ${license.reseller.name}`,
  content: {
    content: <LicenseResellerInfo license={ license } setNavigation={ setNavigation } />,
    key: `lic_${license.id}_reseller`
  }
}) : null;

const getInstitutionPanel = ({ license, setNavigation }) => license.institution ? ({
  title: `Institution: ${license.institution.name}`,
  content: {
    content: <LicenseInstitutionInfo license={ license } setNavigation={ setNavigation } />,
    key: `lic_${license.id}_institution`
  }
}) : null;

const getUpgradesPanel = ({ license, onDeactivateLicenseUpgrade, setNavigation }) => license.license_upgrades.length ? ({
  title: `Upgrades: ${license.license_upgrades.map((lu) => `${lu.upgraded_from} → ${lu.upgraded_to}${lu.status === STATUS.NOT_ACTIVE ? " (Inactive)" : ""}`).join(", ")}`,
  content: {
    content: <LicenseUpgradeInfo license={ license } onDeactivateLicenseUpgrade={onDeactivateLicenseUpgrade} setNavigation={setNavigation} />,
    key: `lic_${license.id}_upgrades`,
  }
}) : null;

const getPanels = ({ license, onModifyLicense, onRemoveLicenseMachine, setNavigation, onAddSublicense, onRemoveSublicense, onDeactivateLicenseUpgrade }) => {
  return [
    getSubLicensesPanel({ license, onAddSublicense, onRemoveSublicense }),
    getUpgradesPanel({ license, onDeactivateLicenseUpgrade, setNavigation }),
    getSettingsPanel(license, onModifyLicense),
    getMachinesPanel({license, onRemoveLicenseMachine}),
    getFastspringPanel({license, setNavigation}),
    getActivationCodePanel(license, setNavigation),
    getResellerPanel({ license, setNavigation }),
    getInstitutionPanel({ license, setNavigation }),
    getSerialPanel(license)
  ]
    .filter(p => p)
}

const ProductActivatedInfoDisplay = R.ifElse(
  R.isNil,
  R.always(null),
  text => (<LineItem label='Created by' text={text} />)
)
const ProductActivatedInfo = R.compose(
  ProductActivatedInfoDisplay,
  ProductActivatedInfoText,
  R.defaultTo({}),
  R.prop('license')
)

const ProductDeactivatedByDisplay = R.ifElse(
  R.isNil,
  R.always(null),
  text => (<LineItem label='Deactivated by' text={text} />)
)

const ProductDeactivatedBy = R.compose(
  ProductDeactivatedByDisplay,
  ProductDeactivatedByText,
  R.defaultTo({}),
  R.prop('license')
)

const ProductDeactivatedOn = ({ license }) => {
  if (license.status === STATUS.ACTIVE || !license.deactivated_on) { return null }
  const formattedDeactivation = moment(license.deactivated_on).format("YYYY-MM-DD");
  return <LineItem label="Deactivated on" text={formattedDeactivation} />;
};

ProductDeactivatedOn.propTypes = {
  license: PropTypes.shape({
    status: PropTypes.string.isRequired,
    deactivated_on: PropTypes.string
  }).isRequired
};

const confirmDeactivate = (onRemove, licenseId) => _e => {
  if (window.confirm('Deactivate this license?')) {
    onRemove(licenseId);
  }
}

const confirmReactivate = (onReactivate, licenseId) => _e => {
  if (window.confirm('Re-activate this license?')) {
    onReactivate(licenseId);
  }
}

const getLicenseStatus = R.compose(
  txt => txt === 'active' ? 'Active' : 'Deactivated',
  R.defaultTo('not_active'),
  R.prop('status')
)

const getSubReference = R.path(['activated_info', 'fastspring', 'subscription', 'reference'])
const getOrderReference = R.compose(
  R.head,
  R.split(' '),
  R.path(['activated_info', 'fastspring', 'reference'])
)
const getDeactivateFsUrl = R.ifElse(
  getSubReference,
  lic => getFsUrl('sub')(getSubReference(lic)),
  lic => getFsUrl('order')(getOrderReference(lic))
)

// allow all lics to transfer except free trial logins and fastspring subs
const canTransfer = R.cond([
  [R.pathEq(['activated_info', 'mechanism'], MECHANISMS.FIRSTLOGIN), R.F],
  [R.T, R.T]
])

const License = ({
  license,
  setNavigation,
  onModifyLicense,
  onReactivateLicense,
  onRemoveLicense,
  onRemoveLicenseMachine,
  onAddSublicense,
  onRemoveSublicense,
  onDeactivateLicenseUpgrade,
}) => {

  const canAdminLicense = useCanAdminLicenses();

  const [transferringLicense, setTransferringLicense] = useState(false);
  const [upgradingLicense, setUpgradingLicense] = useState(false);
  const [licenseRetrievalUrl, setLicenseRetrievalUrl] = useState("");
  const [urlCopied, setUrlCopied] = useState(false);

  const licenseFsReference = R.pathOr(null, ['activated_info', 'fastspring', 'subscription', 'reference'], license) ||
    R.path(['activated_info', 'fastspring', 'reference'], license);

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const getRetrievalUrl = () => {
    setUrlCopied(false);

    superagent
      .get(`${AUTH_API_URL}/licenses/${license._id}/get_ret_code`)
      .withCredentials()
      .then((res) => {
        const url = res.body.retrievalUrl;

        if (url) {
          setLicenseRetrievalUrl(url);
          copyUrl(url);
        }
      });
  };

  const onTransferLicense = (user) => {
    setTransferringLicense(false);

    // Go to the newly transferred license's user
    if (user && user._id) {
      return setNavigation({ pageName: PAGES.USERS, ids: [user._id] });
    }

    // Go to the newly transferred license
    if (license && license._id) {
      return setNavigation({ pageName: PAGES.LICENSES, ids: [license._id] });
    }
  };

  const onUpgradeLicense = () => {
    setUpgradingLicense(false);
    return setNavigation({ pageName: PAGES.LICENSES, ids: [license._id] });
  };

  const panels = getPanels({
    license,
    onModifyLicense,
    onRemoveLicenseMachine,
    setNavigation,
    onAddSublicense,
    onRemoveSublicense,
    onDeactivateLicenseUpgrade,
  });

  return (
    <Segment tertiary={ license.status === STATUS.NOT_ACTIVE || moment(license.expires_on).isBefore(moment()) } >
      <Grid>
        <Grid.Row columns={2}>
          <Grid.Column width={4}>
            <LineItem label='Name' text={ getProductName(license) } />
            <LineItem label='Type' text={getProductType(license)} />
            <LineItem label='Display' text={license.display || "UNDEFINED"} />
            <LineItem label='Status' text={getLicenseStatus(license)} />
            <LineItem label={getProductExpiresLabel(license)} text={getProductExpires(license)} isDate={true} />
            <LineItem label='Upgradable' text={licenseIsUpgradable(license)} />
            <LineItem label='Created on' text={getCreatedOn(license)} isDate={true} />
            <ProductActivatedInfo license={license} />
            <ProductDeactivatedBy license={license} />
            <ProductDeactivatedOn license={license} />
            <LineItem label='Id' text={license._id} small={true} />
            <br/>
            <Button.Group size='mini' style={{ marginRight: "0.5rem" }}>
              {
                canAdminLicense && R.propEq('status', STATUS.ACTIVE, license) && !R.pathEq(['activated_info', 'mechanism'], 'fastspring', license)
                  ? (<Button negative content='Deactivate' size='mini' onClick={confirmDeactivate(onRemoveLicense, {licenseId: license._id})} />)
                  : null
              }

              {
                canAdminLicense && R.propEq('status', STATUS.ACTIVE, license) && R.pathEq(['activated_info', 'mechanism'], 'fastspring', license)
                  ? (<Button
                    as='a'
                    content='Deactivate in FastSpring'
                    negative
                    size='mini'
                    href={getDeactivateFsUrl(license)}
                    target='_blank'
                  />)
                  : null
              }

              {
                canAdminLicense && license.status === STATUS.NOT_ACTIVE ? (
                  <Button
                    positive
                    content="Re-activate"
                    size="mini"
                    onClick={confirmReactivate(onReactivateLicense, license._id)}
                  />
                ) : null
              }

              {
                canAdminLicense && canTransfer(license)
                  ? <Button primary content='Transfer' size='mini' onClick={() => setTransferringLicense(true)} disabled={transferringLicense}/>
                  : null
              }
            </Button.Group>

            {canAdminLicense && licenseIsUpgradable(license) ? (
              <Button.Group size="mini" style={{ marginTop: "0.5rem" }}>
                <Button
                  size="mini"
                  color="green"
                  content={`Upgrade to ${MAX_VERSIONS_DISPLAYNAME[CURRENT_MAX_VERSION]}`}
                  onClick={() => setUpgradingLicense(true)}
                  disabled={upgradingLicense}
                />
              </Button.Group>
            ) : null}
          </Grid.Column>
          <Grid.Column width={12}>
            {transferringLicense ? (
              <Transfer
                license={license}
                onTransferLicense={onTransferLicense}
                onCancel={() => setTransferringLicense(false)}
              />
            ) : upgradingLicense ? (
              <Upgrade
                license={license}
                onUpgradeLicense={onUpgradeLicense}
                onCancel={() => setUpgradingLicense(false)}
              />
            ) : (
              <Accordion
                defaultActiveIndex={0}
                panels={panels}
                styled
                fluid
                exclusive={false}
              />
            )}

            {!license.user && licenseFsReference && !licenseRetrievalUrl ? (
              <div style={{ paddingTop: "1em" }}>
                <Button
                  content="Get license retrieval URL"
                  size="mini"
                  onClick={getRetrievalUrl}
                />
              </div>
            ) : null}

            {licenseRetrievalUrl ? (
              <Menu secondary>
                <Menu.Item>
                  <LineItem
                    small
                    label="Retrieval URL"
                    text={licenseRetrievalUrl}
                    onClick={() => copyUrl(licenseRetrievalUrl)}
                  />
                  {urlCopied ? (
                    <Label color="violet" horizontal>Copied to clipboard</Label>
                  ) : null}
                </Menu.Item>
              </Menu>
            ) : null}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

License.propTypes = {
  license: PropTypes.object.isRequired,
  setNavigation: PropTypes.func.isRequired,
  onModifyLicense: PropTypes.func.isRequired,
  onReactivateLicense: PropTypes.func.isRequired,
  onRemoveLicense: PropTypes.func.isRequired,
  onRemoveLicenseMachine: PropTypes.func.isRequired,
  onAddSublicense: PropTypes.func.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired,
  onDeactivateLicenseUpgrade: PropTypes.func.isRequired,
}

export default License



// WEBPACK FOOTER //
// ./webAdmin/views/license/index.js